'use client';

import React, { useState, useCallback, useEffect } from 'react';
import { Box, Text, Button, RadioGroup, Flex } from '@shared/ui';
import i18n from '@shared/i18n';
import { getDynamicImageSource } from 'app/libs/url';
import { Picture } from 'app/components/Picture';

interface SpecSelectionProps {
    addonIndex: number;
    specDetails: (ProductSpecDetail | BuyerPromoProductSpecDetail)[];
    specMetas: BuyerPromoProduct['spec_metas'];
    onSelect: (spec: ProductSpecDetail | BuyerPromoProductSpecDetail) => void;
    selectedSpecs: (ProductSpecDetail | BuyerPromoProductSpecDetail)[];
    quantity?: number;
}

const specKeyMap = {
    spec_1: 'spec_1',
    spec_2: 'spec_2',
    spec_3: 'spec_3',
} as const;

export const SpecSelection = ({
    addonIndex,
    specDetails,
    specMetas,
    onSelect,
    selectedSpecs,
    quantity = 1,
}: SpecSelectionProps) => {
    const [localSelectedSpecs, setLocalSelectedSpecs] = useState(() => {
        const selectedSpec = selectedSpecs?.[addonIndex];

        if (selectedSpec) {
            return {
                [specKeyMap.spec_1]: selectedSpec.spec_1 || '',
                [specKeyMap.spec_2]: selectedSpec.spec_2 || '',
                [specKeyMap.spec_3]: selectedSpec.spec_3 || '',
            };
        }

        return {
            [specKeyMap.spec_1]: '',
            [specKeyMap.spec_2]: '',
            [specKeyMap.spec_3]: '',
        };
    });

    const [showAllSpecs, setShowAllSpecs] = useState({
        spec_1: false,
        spec_2: false,
        spec_3: false,
    });
    const [availableStock, setAvailableStock] = useState<Record<string, number>>({});

    const specOptions = Object.fromEntries(
        specMetas.map((meta, index) => [
            `spec_${index + 1}` as keyof typeof specKeyMap,
            meta.specs.map((spec) => spec.name),
        ])
    ) as Record<keyof typeof specKeyMap, string[]>;

    useEffect(() => {
        const newAvailableStock: Record<string, number> = {};

        specDetails.forEach((spec) => {
            const key = `${spec.spec_1}-${spec.spec_2}-${spec.spec_3}`;

            // 從庫存中減去已加入購物車的數量
            if ('cart_quantity' in spec) {
                newAvailableStock[key] = spec.stock - (spec.cart_quantity || 0);
            } else {
                newAvailableStock[key] = spec.stock;
            }
        });

        if (selectedSpecs) {
            selectedSpecs.forEach((selectedSpec) => {
                if (!selectedSpec) return;
                const key = `${selectedSpec.spec_1}-${selectedSpec.spec_2}-${selectedSpec.spec_3}`;

                if (newAvailableStock[key] !== undefined && newAvailableStock[key] > 0) {
                    newAvailableStock[key] -= 1;
                }
            });
        }

        setAvailableStock(newAvailableStock);
    }, [specDetails, selectedSpecs]);

    // 根據已選擇的規格，過濾出可選擇的規格選項
    const getAvailableOptions = useCallback(
        (specKey: keyof typeof localSelectedSpecs) => {
            return specDetails.filter((spec) => {
                const otherKeys = Object.values(specKeyMap).filter(
                    (key) => key !== specKey
                );

                return otherKeys.every(
                    (key) =>
                        !localSelectedSpecs[key] || spec[key] === localSelectedSpecs[key]
                );
            });
        },
        [specDetails, localSelectedSpecs]
    );

    // 判斷規格選項是否應該被 disabled（無庫存或不可選）
    const isOptionDisabled = useCallback(
        (specKey: keyof typeof localSelectedSpecs, value: string) => {
            const availableOptions = getAvailableOptions(specKey);

            return !availableOptions.some((option) => {
                const key = `${option.spec_1}-${option.spec_2}-${option.spec_3}`;

                return option[specKey] === value && availableStock[key] >= quantity;
            });
        },
        [getAvailableOptions, availableStock, quantity]
    );

    // 更新選擇的規格
    const handleSpecSelect = useCallback(
        (specKey: keyof typeof localSelectedSpecs, value: string) => {
            setLocalSelectedSpecs((prev) => {
                const newSpecs = { ...prev, [specKey]: value };

                Object.keys(newSpecs).forEach((key) => {
                    if (key !== specKey && newSpecs[key]) {
                        const conflictingOptions = getAvailableOptions(
                            key as keyof typeof localSelectedSpecs
                        );

                        if (
                            !conflictingOptions.some(
                                (option) => option[key] === newSpecs[key]
                            )
                        ) {
                            newSpecs[key] = '';
                        }
                    }
                });

                const selectedSpecDetail = specDetails.find(
                    (spec) =>
                        spec.spec_1 === newSpecs.spec_1 &&
                        spec.spec_2 === newSpecs.spec_2 &&
                        spec.spec_3 === newSpecs.spec_3
                );

                if (selectedSpecDetail) {
                    const key = `${selectedSpecDetail.spec_1}-${selectedSpecDetail.spec_2}-${selectedSpecDetail.spec_3}`;

                    if (availableStock[key] > 0) {
                        onSelect(selectedSpecDetail);
                    }
                }

                return newSpecs;
            });
        },
        [specDetails, onSelect, getAvailableOptions, availableStock]
    );

    const renderSpecOptions = useCallback(
        (
            specKey: keyof typeof localSelectedSpecs,
            options: string[],
            specMeta: BuyerPromoProduct['spec_metas'][number]
        ) => {
            const visibleOptions = showAllSpecs[specKey] ? options : options.slice(0, 5);
            const remainingOptionsCount = options.length - 5;

            return (
                <>
                    <Text fontWeight="bold" mb="xs">
                        {specMeta.group_name || ''}
                    </Text>
                    <RadioGroup value={localSelectedSpecs[specKey]}>
                        <Flex flexWrap="wrap">
                            {visibleOptions.map((spec, index) => {
                                const isDisabled = isOptionDisabled(specKey, spec);
                                const isSelected = localSelectedSpecs[specKey] === spec;

                                let bgColor = 'neutral.000';
                                let textColor = 'neutral.130';

                                if (isSelected) {
                                    bgColor = 'dynamic.010';
                                    textColor = 'dynamic.050';
                                } else if (isDisabled) {
                                    bgColor = 'neutral.020';
                                    textColor = 'neutral.070';
                                }

                                const { image } = specMeta.specs[index];
                                const source = image.src
                                    ? getDynamicImageSource(image.src, 100, 100)
                                    : null;

                                return (
                                    <Flex
                                        key={spec}
                                        px="s"
                                        py="xs"
                                        as="label"
                                        border="1px solid"
                                        borderColor={
                                            isSelected ? 'dynamic.010' : 'neutral.040'
                                        }
                                        borderRadius="2px"
                                        bg={bgColor}
                                        mr="xs"
                                        mb="xs"
                                        minW="36px"
                                        minH="36px"
                                        cursor={
                                            isDisabled && !isSelected
                                                ? 'not-allowed'
                                                : 'pointer'
                                        }
                                        maxW="334px"
                                        gap="xxs"
                                    >
                                        {source && (
                                            <Box w="20px" h="20px" flexShrink="0">
                                                <Picture
                                                    src={source.src}
                                                    sources={[
                                                        {
                                                            srcSet: source.srcSet,
                                                            type: source.type,
                                                        },
                                                    ]}
                                                    alt={spec}
                                                />
                                            </Box>
                                        )}
                                        <input
                                            type="radio"
                                            value={spec}
                                            checked={isSelected}
                                            onChange={() =>
                                                handleSpecSelect(specKey, spec)
                                            }
                                            disabled={isDisabled}
                                            style={{ display: 'none' }}
                                        />
                                        <Text
                                            overflow="hidden"
                                            whiteSpace="nowrap"
                                            textOverflow="ellipsis"
                                            color={textColor}
                                        >
                                            {spec}
                                        </Text>
                                    </Flex>
                                );
                            })}
                        </Flex>
                        {!showAllSpecs[specKey] && options.length > 5 && (
                            <Box w="fit-content">
                                <Button
                                    onClick={() =>
                                        setShowAllSpecs((prev) => ({
                                            ...prev,
                                            [specKey]: true,
                                        }))
                                    }
                                    variant="outline"
                                    boxShadow="none"
                                    border="none"
                                    color="dynamic.040"
                                >
                                    {i18n.gettext('展開更多規格') +
                                        ` (${remainingOptionsCount})`}
                                </Button>
                            </Box>
                        )}
                    </RadioGroup>
                </>
            );
        },
        [showAllSpecs, localSelectedSpecs, isOptionDisabled, handleSpecSelect]
    );

    return (
        <Box bg="neutral.010" px="s" py="xs">
            {Object.values(specKeyMap).map((specKey, index) => {
                if (specOptions[specKey]?.length > 0) {
                    return (
                        <React.Fragment key={specKey}>
                            {renderSpecOptions(
                                specKey,
                                specOptions[specKey],
                                specMetas[index]
                            )}
                        </React.Fragment>
                    );
                }

                return null;
            })}
        </Box>
    );
};
